import { setDialog, setSession, setSnackbar } from '@shared/library';
import axios, { AxiosError } from 'axios';
import React from 'react';
import packageJson from '../../../../../package.json';
import config from '../../../../config';
import { customInterventionColumns, customInterventionEditForm } from '../../../../config/_custom/WFM/WfmAtena';
import { loadCitiesAndSelected } from '@webgest/library';
//#region COMPONENTS
import { Fade } from '@mui/material';
import GestDialog from '@shared/library/dist/components/gest/GestDialog/GestDialog';
import GestSnackbar from '@shared/library/dist/components/gest/GestSnackbar/GestSnackbar';
import AlarmsManager from '@webgest/alarms-manager';
import GeoTool from '@webgest/geotool';
import RoutesAnalyzer from '@webgest/route-analyzer';
import BillingAnalyzer from '@webgest/billing-analyzer';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import AdministrationPanel from './Modules/AdminTools/AdministrationPanel/AdministrationPanel';
import ToPolyline from './Modules/AdminTools/ToPolyline/ToPolyline';
import DynamicDashboard from './Modules/DevExpressDashboards/DynamicDashboard';
import GetOld from './Modules/GetOld';
import Home from './Modules/Home/Home';
import RouteGenerator from './Modules/WalkBy-DriveBy/RouteGenerator/RouteGenerator';
import InterventionManagement from './Modules/WFM/InterventionManagement/InterventionManagement';
import PlanAssignments from './Modules/WFM/PlanAssignment/PlanAssignments';
import Planning from './Modules/WFM/Planning/Planning';
//#endregion
//CONTEXTS 
import { CntxConfig } from '../../../WebGest';

const AllowedRoutes = () => {
    //CONTEXTS USE
    const cntxConfig = React.useContext(CntxConfig)!;

    //STATIC DATA
    const routerLocation = useLocation();

    //STATES
    const [inProp, setInProp] = React.useState(true);
    const pollingLoading = React.useRef(false);

    //#region METHODS
    const checkPollingStatus = (endpoint: "Auth/SessionValid" | `Gest/Software/WebGest/frontend/${string}/${string}/IsChanged`) =>
        axios.get<{ token: string; } | boolean>(`${config.api.internal}/${endpoint}`, { withCredentials: true })
            .then(res => res.data)
            .catch((error: AxiosError) => endpoint === "Auth/SessionValid" ? error?.status !== 401 : false);

    const doPollingChecks = () => setInterval(async () => {
        if (!pollingLoading.current) {
            pollingLoading.current = true;
            //check session
            const isLoggedIn = await checkPollingStatus("Auth/SessionValid");

            if (!isLoggedIn) {
                setSnackbar(cntxConfig, { message: "Sessione scaduta. Ritorno alla login in corso...", severity: "error" });
                setTimeout(() => setSession(cntxConfig), 3_000);
            }
            else {
                //check version
                const isVersionChanged = await checkPollingStatus(`Gest/Software/WebGest/frontend/${process.env.REACT_APP_ENV ?? "sviluppo"}/${packageJson.version}/IsChanged`);
                if (isVersionChanged) {
                    setSnackbar(cntxConfig, { message: "Aggiornamento della versione. E' necessario ricaricare la pagina", severity: "info" });
                    setTimeout(() => window.location.reload(), 3_000);
                }
            }

            pollingLoading.current = false;
        }
    }, 60_000);//1 minute
    //#endregion

    //#region STATES CHANGE
    React.useEffect(() => {
        loadCitiesAndSelected(config.api.internal, cntxConfig);

        let interval: NodeJS.Timeout | undefined;

        if (cntxConfig.session?.userData) {
            interval = doPollingChecks();
        }

        return () => clearInterval(interval);
    }, [cntxConfig.session?.userData]);

    React.useEffect(() => {
        setInProp(false);
        const timeout = setTimeout(() => setInProp(true), 500);
        return () => clearTimeout(timeout);
    }, [routerLocation]);
    //#endregion

    return cntxConfig?.session && !cntxConfig?.cache?.cities.loading &&
        <Fade in={inProp} timeout={400}>
            <div className="pages">
                <Routes>
                    <Route path={`/`} element={<Home {...cntxConfig} />} />

                    {/* NETWORK ANALYSIS */}
                    <Route path={`/dynamicDashboard/:id?`} element={<DynamicDashboard />} />
                    <Route path={`/networkAnalysis`} element={<GetOld path={`${config.api.old}/networkAnalysis`} />} />

                    {/* MONITOR */}
                    <Route path={`/consumptionMonitor`} element={<GetOld path={`${config.api.old}/consumptionMonitor`} />} />
                    <Route path={`/pressuresMonitor`} element={<GetOld path={`${config.api.old}/pressuresMonitor`} />} />

                    {/* WALK-BY/DRIVE-BY TOOLS */}
                    <Route path={`/RouteGenerator`} element={<RouteGenerator {...cntxConfig} />} />
                    <Route path={`/routesAnalysis`} element={<RoutesAnalyzer {...cntxConfig} />} />
                    <Route path={`/walkByDriveBySync`} element={<GetOld path={`${config.api.old}/walkByDriveBySync`} />} />
                    <Route path={`/import/*`} element={<GetOld path={`${config.api.old}/import/*`} />} />
                    <Route path={`/export/*`} element={<GetOld path={`${config.api.old}/export/*`} />} />

                    {/* OPERATING TOOLS */}
                    <Route path={`/userRegistries`} element={<GetOld path={`${config.api.old}/userRegistries`} />} />

                    {/* FIELD DATA SURVEY */}
                    <Route path={`/qualifications`} element={<GetOld path={`${config.api.old}/qualifications`} />} />   {/* Da cambiare in /fieldDataMonitor */}

                    {/* WORKFORCE MANAGEMENT (WFM)*/}
                    <Route path={`/interventionManagement`} element={<InterventionManagement {...{ config: cntxConfig }} />} />
                    <Route path={`/InterventionManagementATENA`} element={<InterventionManagement {...{ config: cntxConfig, customInterventionEditForm, customInterventionColumns }} />} /> {/* CUSTOMIZATION */}
                    <Route path={`/planning`} element={<Planning {...cntxConfig} />} />
                    <Route path={`/planAssignments`} element={<PlanAssignments {...cntxConfig} />} />

                    {/* ADMIN TOOLS */}
                    <Route path={`/adminTools/toPolyline`} element={<ToPolyline />} />
                    <Route path={`/GeoTool`} element={<GeoTool {...cntxConfig} />} />
                    <Route path={`/alarms-manager`} element={<AlarmsManager {...cntxConfig} />} />
                    <Route path={`/billing-analyzer`} element={<BillingAnalyzer {...cntxConfig} />} />

                    {/* ADMINISTRATION PANEL INTEGRATION (admins only)*/}
                    {cntxConfig.session.userData?.role === 'admin' && <Route path={`/AdminPanel`} element={<AdministrationPanel {...cntxConfig} />} />}

                    <Route path="*" element={<Navigate to={`/`} />} />
                </Routes>

                <GestSnackbar {...cntxConfig} />
                <GestDialog {...{ ...cntxConfig.dialog, setModal: dialog => setDialog(cntxConfig, dialog) }} />
            </div >
        </Fade>;
};
export default React.memo(AllowedRoutes);